import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { BadgeEtape, Pastille } from "./badges";
import type { Fichier, Pari } from "./contract";
import { ageEnClair, estAncien, formatDate, scoreEnClair, typeEnClair } from "./format";
import { dossierIdee, GITHUB_BLOB, renderMarkdown } from "./markdown";
import { useIdee } from "./queries";
import { ConsoleShell } from "./shell";
import { Chargement, EcranDErreur } from "./states";

/**
 * Le detail d'une idee : le pari en tete, puis un onglet par fichier du dossier.
 *
 * Les liens relatifs du markdown qui designent un fichier du dossier sont reecrits en
 * `#<nom du fichier>` (voir `markdown.ts`). La page ecoute donc `hashchange` pour ouvrir
 * l'onglet correspondant : un lien ordinaire, sans gestionnaire de clic pose sur du HTML
 * qui vient d'un fichier, et qui continue de marcher si le script n'a pas repris la main.
 */

export function IdeePage({ slug, maintenant = Date.now() }: { slug: string; maintenant?: number }) {
  const idee = useIdee(slug);

  return (
    <ConsoleShell connecte={idee.isSuccess}>
      <p className="text-sm text-muted-foreground">
        <Link className="underline underline-offset-2" to="/">
          Portefeuille
        </Link>{" "}
        / {slug}
      </p>
      {idee.isPending ? <Chargement quoi="de l'idée" /> : null}
      {idee.isError ? <EcranDErreur error={idee.error} quoi={`l'idée ${slug}`} /> : null}
      {idee.data ? (
        <>
          <EnTete maintenant={maintenant} pari={idee.data.pari} slug={slug} />
          <Onglets fichiers={idee.data.fichiers} slug={slug} />
        </>
      ) : null}
    </ConsoleShell>
  );
}

function EnTete({ pari, slug, maintenant }: { pari: Pari; slug: string; maintenant: number }) {
  const ouverts = pari.prerequis.filter((geste) => !geste.fait).length;

  return (
    <>
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">{pari.titre}</h1>
        <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <BadgeEtape etape={pari.etape} />
          {pari.piste ? <span>piste {pari.piste}</span> : null}
          {pari.liens.issue ? (
            <a
              className="underline underline-offset-2"
              href={`https://github.com/kavaliero/ai-revenue-lab/issues/${pari.liens.issue}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              issue #{pari.liens.issue}
            </a>
          ) : null}
          <a
            className="underline underline-offset-2"
            href={`${GITHUB_BLOB}docs/idees/${slug}/`}
            rel="noopener noreferrer"
            target="_blank"
          >
            dossier docs/idees/{slug}/
          </a>
          {pari.liens.spec ? (
            <a
              className="underline underline-offset-2"
              href={`${GITHUB_BLOB}${pari.liens.spec}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              spec
            </a>
          ) : null}
          <span>mise à jour le {formatDate(pari.maj)}</span>
        </p>
      </header>

      <section className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="flex flex-col gap-4 pt-6">
            <p className="text-2xl font-semibold">
              {scoreEnClair(pari.score, pari.plafond) || "score non enregistré"}
            </p>
            <Champ libelle="Verdict">{pari.verdict ?? "aucun"}</Champ>
            <Champ libelle="Pourquoi">{pari.pourquoi ?? "non renseigné"}</Champ>
            <div className="grid gap-4 sm:grid-cols-2">
              <Champ libelle="Déclencheur">{pari.declencheur ?? "aucun"}</Champ>
              <Champ libelle="Prochaine date">
                {pari.prochaineDate ? formatDate(pari.prochaineDate) : "aucune"}
              </Champ>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Prérequis</CardTitle>
          </CardHeader>
          <CardContent>
            {pari.prerequis.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun geste en attente.</p>
            ) : (
              <>
                <p className="pb-2 text-sm text-muted-foreground">
                  {ouverts} ouvert{ouverts > 1 ? "s" : ""} sur {pari.prerequis.length}.
                </p>
                <ul className="flex flex-col gap-2">
                  {pari.prerequis.map((geste) => (
                    <li
                      className="grid gap-x-3 gap-y-1 text-sm sm:grid-cols-[4rem_5.5rem_minmax(0,1fr)_7rem] sm:items-baseline"
                      key={geste.quoi}
                    >
                      <span className="text-muted-foreground">
                        {geste.fait ? "fait" : "à faire"}
                      </span>
                      <span>
                        <Pastille>{typeEnClair(geste.type)}</Pastille>
                      </span>
                      {/* Le texte du geste mene a son guide. Un vieux prerequis sans
                          guide reste du texte : aucun fichier ne l'attend derriere. */}
                      {geste.guide === null ? (
                        <span className="min-w-0">{geste.quoi}</span>
                      ) : (
                        <Link
                          className="min-w-0 font-medium underline underline-offset-2"
                          params={{ nom: geste.guide, slug }}
                          to="/portefeuille/idees/$slug/gestes/$nom"
                        >
                          {geste.quoi}
                        </Link>
                      )}
                      {/* Le rouge ne vaut que pour ce qui attend encore : un geste fait il y
                          a un mois n'a rien d'urgent. */}
                      <span
                        className={
                          !geste.fait && estAncien(geste.depuis, maintenant)
                            ? "font-medium text-destructive sm:text-right"
                            : "text-muted-foreground sm:text-right"
                        }
                      >
                        {ageEnClair(geste.depuis, maintenant)}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardContent>
        </Card>
      </section>
    </>
  );
}

function Champ({ libelle, children }: { libelle: string; children: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase text-muted-foreground">{libelle}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

/** Le nom de fichier porte par l'ancre courante, quand il designe un fichier du dossier. */
function ongletDuFragment(fichiers: readonly Fichier[]): string | null {
  if (typeof window === "undefined") return null;
  const fragment = window.location.hash.replace(/^#/, "");
  if (fragment === "") return null;
  let nom = fragment;
  try {
    nom = decodeURIComponent(fragment);
  } catch {
    // Fragment mal encode : on s'en tient au texte brut.
  }
  return fichiers.some((fichier) => fichier.nom === nom) ? nom : null;
}

function Onglets({ fichiers, slug }: { fichiers: readonly Fichier[]; slug: string }) {
  const premier = fichiers[0]?.nom ?? "";
  const [actif, setActif] = useState(premier);

  useEffect(() => {
    const suivre = (): void => {
      const demande = ongletDuFragment(fichiers);
      if (demande !== null) setActif(demande);
    };
    suivre();
    window.addEventListener("hashchange", suivre);
    return () => window.removeEventListener("hashchange", suivre);
  }, [fichiers]);

  // Un lien du markdown vers un onglet est un `#<nom>` ordinaire, que `hashchange` suit.
  // Mais quand le fragment vaut deja ce nom (onglet quitte par un bouton, puis le meme
  // lien reclique), aucun `hashchange` ne part. Ce gestionnaire, pose sur notre conteneur
  // et jamais sur le HTML venu du fichier, ouvre l'onglet dans ce cas aussi.
  const panneau = useRef<HTMLElement>(null);
  useEffect(() => {
    const element = panneau.current;
    if (element === null) return;
    const ouvrir = (event: MouseEvent): void => {
      const ancre = event.target instanceof Element ? event.target.closest("a[data-onglet]") : null;
      const nom = ancre?.getAttribute("data-onglet") ?? null;
      if (nom !== null && fichiers.some((fichier) => fichier.nom === nom)) setActif(nom);
    };
    element.addEventListener("click", ouvrir);
    return () => element.removeEventListener("click", ouvrir);
  }, [fichiers]);

  const courant = fichiers.find((fichier) => fichier.nom === actif) ?? fichiers[0];
  const contexte = useMemo(
    () => ({ dossier: dossierIdee(slug), fichiers: fichiers.map((fichier) => fichier.nom) }),
    [slug, fichiers],
  );
  const html = useMemo(
    () => (courant ? renderMarkdown(courant.markdown, contexte) : ""),
    [courant, contexte],
  );

  if (fichiers.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          Le dossier ne contient aucun fichier markdown.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-wrap gap-1 border-b border-border p-2" role="tablist">
        {fichiers.map((fichier) => (
          <button
            aria-selected={fichier.nom === courant?.nom}
            className={`rounded-md px-3 py-1.5 text-sm ${
              fichier.nom === courant?.nom ? "bg-muted font-semibold" : "text-muted-foreground"
            }`}
            key={fichier.nom}
            onClick={() => setActif(fichier.nom)}
            role="tab"
            type="button"
          >
            {fichier.nom}
          </button>
        ))}
      </div>
      {/* Le HTML vient de `renderMarkdown` : marked, puis DOMPurify, puis reecriture des
          liens. C'est le seul endroit de la console ou du HTML est pose tel quel, et il
          ne sort jamais de cette chaine. */}
      <article
        className="markdown flex flex-col gap-3 p-4 text-sm"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: markdown du depot, deja passe par DOMPurify dans renderMarkdown
        dangerouslySetInnerHTML={{ __html: html }}
        ref={panneau}
        role="tabpanel"
      />
    </Card>
  );
}
